// pages/login.js - Updated version
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuthLoading, useAuthStatus } from "context/OAuth/AuthContext";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { getLoginMarketingLinks } from "@constants/loginMarketingLinks";
import useAuthLang from "@hooks/useAuthLang";
import { authPath } from "utils/authLanguage";

const links = getLoginMarketingLinks();

const validationSchema = Yup.object({
  email: Yup.string().trim().required("Username or Email is required"),
  password: Yup.string().required("Password is required"),
});

const Login = () => {
  const { t } = useTranslation();
  const authLang = useAuthLang();
  const [showPassword, setShowPassword] = useState(false);
  const { login, completeLoginWithToken } = useAuthStatus();
  const { setIsAuthLoading } = useAuthLoading();
  const navigate = useNavigate();

  // Get environment variables
  const appDomain = process.env.REACT_APP_SSO_APP_URL;

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { ok, data } = await login({
          email: values.email.trim(),
          password: values.password,
        });

        const ssoToken = data?.sso?.token;
        if (!ok || !ssoToken) {
          toast.error(
            typeof data?.message === "string"
              ? data.message
              : t("Login failed. Check your credentials."),
          );
          return;
        }

        await completeLoginWithToken(ssoToken);
        setIsAuthLoading(true);
        navigate("/");
      } catch (err) {
        console.error(err);
        toast.error(t("Something went wrong. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
          <h1 className="text-center text-2xl sm:text-[30px] font-[600] text-[#181D27] mb-2">
            {t("Log in to your account")}
          </h1>
          <p className="text-center text-[#535862] text-sm sm:text-base mb-8">
            {t("Welcome back! Please enter your details.")}
          </p>

          <form onSubmit={formik.handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-[#414651] mb-1">
                {t("Email")}{" "}
                <span className="text-red-500" aria-hidden>
                  *
                </span>
              </label>
              <input
                type="text"
                name="email"
                autoComplete="username"
                placeholder={t("Email")}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`w-full px-3 py-3 border rounded-lg text-[#1B1F26] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15AAFC] focus:border-[#15AAFC] ${
                  formik.touched.email && formik.errors.email
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300"
                }`}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="text-red-500 text-xs mt-1 font-medium">
                  {formik.errors.email}
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-sm text-[#414651] mb-1">
                {t("Password")}{" "}
                <span className="text-red-500" aria-hidden>
                  *
                </span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder={t("Password")}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full px-3 py-3 pr-11 border rounded-lg text-[#1B1F26] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15AAFC] focus:border-[#15AAFC] ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-500 ring-1 ring-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {formik.touched.password && formik.errors.password ? (
                <div className="text-red-500 text-xs mt-1 font-medium">
                  {formik.errors.password}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-3 text-sm">
              <Link
                to={authPath("password-reset", authLang)}
                className="text-[#0066EE] font-[600] shrink-0"
              >
                {t("Forgot Password")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-base disabled:opacity-70 disabled:cursor-not-allowed transition shadow-md hover:shadow-lg"
              style={{
                background:
                  "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
              }}
            >
              {formik.isSubmitting ? <ButtonLoader /> : t("Login")}
            </button>
          </form>

          <p className="text-center text-sm text-[#535862] mt-8">
            {t("Don't have an account?")}{" "}
            <a
              href={links.signUp}
              className="text-[#0066EE] font-[600]"
              rel="noopener noreferrer"
            >
              {t("Sign up")}
            </a>
          </p>

    </>
  );
};

export default Login;